import React from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import ReviewerQuestionnairesHeader from './ReviewerQuestionnairesHeader';
import QuestionCard from './QuestionCard';
import { ReviewerProvider, useReviewerContext } from '../../context/ReviewerContext';

// ─── Component ─────────────────────────────────────────────────────────────

const ReviewerQuestionnairesContent = () => {
  
  // Get everything from context
  const {
    // States
    loading,
    error,
    questionsLoading,
    selectAll,
    currentPage,
    
    // Computed values
    paginatedQuestions,
    allReviewed,
    anySelected,
    anyPending,
    totalPages,
    
    // Setters
    setCurrentPage,
    
    // Functions
    handleUndoAll,
    handleApproveAll,
    handleRejectAll,
    handleApproveSelected,
    handleRejectSelected,
    toggleSelectAll,
    crosswalkLoading,
  } = useReviewerContext();

  // ── Render ────────────────────────────

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div className=" p-5 bg-white cardStyle">
          <div className="flex items-center justify-between mb-4">
            <div className="min-w-0">
              <div className="w-48 h-6 bg-[#e6eef5] animate-pulse mb-2" />
              <div className="w-64 h-4 bg-[#e6eef5] animate-pulse" />
            </div>
            <div className="flex items-center gap-3">
              <div className="w-28 h-10 bg-[#e6eef5] animate-pulse" />
              <div className="w-40 h-10 bg-[#e6eef5] animate-pulse" />
            </div>
          </div>

          {/* Scrollable cards skeleton */}
          <div className="flex items-center gap-2">
            <button className="p-1 rounded-full text-[#919191] flex-shrink-0">
              <div className="w-6 h-6 rounded-full bg-[#e6eef5] animate-pulse" />
            </button>
            <div className="flex gap-3 overflow-x-auto flex-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="min-w-[170px] max-w-[190px]  p-3 bg-white border border-[#e6eef5]">
                  <div className="w-32 h-4 bg-[#e6eef5] animate-pulse mb-3" />
                  <div className="w-full h-3 bg-[#e6eef5] animate-pulse mb-2" />
                  <div className="w-full h-3 bg-[#e6eef5] animate-pulse" />
                </div>
              ))}
            </div>
            <button className="p-1 rounded-full text-[#919191] flex-shrink-0">
              <div className="w-6 h-6 rounded-full bg-[#e6eef5] animate-pulse" />
            </button>
          </div>
        </div>

        {/* Auto-selected answers skeleton */}
        <div className=" p-5 bg-white cardStyle">
          <div className="flex items-center justify-between mb-4">
            <div className="w-48 h-5 bg-[#e6eef5] animate-pulse" />
            <div className="w-32 h-8 bg-[#e6eef5] animate-pulse" />
          </div>



          {/* Questions list skeleton */}
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="relative border-2  p-6">


                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0">
                    <div className="w-28 h-4 bg-[#e6eef5] animate-pulse mb-2" />
                    <div className="w-40 h-3 bg-[#e6eef5] animate-pulse" />
                  </div>
                  <div className="w-20 h-8 bg-[#e6eef5] animate-pulse" />
                </div>

                {/* Options skeleton */}
                <div className="grid grid-cols-1 gap-3">
                  {Array.from({ length: 3 }).map((_, j) => (
                    <div key={j} className="flex items-center gap-4">
                      <div className="w-4 h-4 rounded-full bg-[#e6eef5] animate-pulse" />
                      <div className="w-40 h-3 bg-[#e6eef5] animate-pulse" />
                    </div>
                  ))}
                </div>

                {/* Confidence label skeleton */}
                <div className="mt-4">
                  <div className="w-24 h-3 bg-[#e6eef5] animate-pulse mb-2" />
                  <div className="flex items-center gap-3">
                    {Array.from({ length: 5 }).map((_, k) => (
                      <div key={k} className="w-14 h-3 bg-[#e6eef5] animate-pulse" />
                    ))}
                  </div>
                </div>

                {/* Undo button skeleton bottom-left */}
                <div className="mt-4">
                  <div className="w-20 h-8 bg-[#e6eef5] animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-red-500 text-sm">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-5 relative">
      {/* ─── Header: Questionnaires ─── */}
      <ReviewerQuestionnairesHeader />

      {/* ─── Auto-Selected Answers Section ─── */}
      <div className=" p-5 bg-white cardStyle">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-[#0077b5]">
              Auto-Selected Answers
            </h3>
            {crosswalkLoading && (
              <span className="flex items-end gap-[3px] pb-[2px]">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    style={{ animationDelay: `${i * 0.18}s` }}
                    className="block w-[6px] h-[6px] rounded-full bg-[#0077b5] animate-bounce"
                  />
                ))}
              </span>
            )}
          </div>
          {allReviewed && (
            <button
              onClick={handleUndoAll}
              className="px-5 py-2 text-sm font-semibold bg-[#F47F2E] text-white hover:bg-[#F47F2E] transition-colors"
            >
              Undo all
            </button>
          )}
        </div>

        {/* Subtitle */}
        <p className="text-sm text-[#919191] mb-4">Accept or reject answers suggested by Crosswalk, or change the answer yourself. Click <span className="font-semibold text-[#53545a]">Show Explanation</span> to see why Crosswalk chose that answer.</p>

        {/* Select All + Approve All / Reject All */}
        {anyPending && (
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#919191]/20">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={toggleSelectAll}
                className="w-4 h-4 accent-[#F47F2E] cursor-pointer"
              />
              <span className="text-sm font-medium text-[#53545a]">
                Select All
              </span>
            </label>
            <div className="flex gap-2">
              <button
                onClick={handleApproveAll}
                className="px-4 py-1.5 text-xs font-semibold bg-[#0077b5] text-white hover:bg-[#0077b5]/90 transition-colors"
              >
                Approve All
              </button>
              <button
                onClick={handleRejectAll}
                className="px-4 py-1.5 text-xs font-semibold bg-[#F47F2E] text-white hover:bg-[#F47F2E] transition-colors"
              >
                Reject All
              </button>
            </div>
          </div>
        )}

        {/* Questions List */}
        <div className="space-y-4">

          {questionsLoading ? (

            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="relative border-2  p-6">


                  <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0">
                      <div className="w-28 h-4 bg-[#e6eef5] animate-pulse mb-2" />
                      <div className="w-40 h-3 bg-[#e6eef5] animate-pulse" />
                    </div>
                    <div className="w-20 h-8 bg-[#e6eef5] animate-pulse" />
                  </div>

                  {/* Options skeleton */}
                  <div className="grid grid-cols-1 gap-3">
                    {Array.from({ length: 3 }).map((_, j) => (
                      <div key={j} className="flex items-center gap-4">
                        <div className="w-4 h-4-full bg-[#e6eef5] animate-pulse" />
                        <div className="w-40 h-3 bg-[#e6eef5] animate-pulse" />
                      </div>
                    ))}
                  </div>

                  {/* Confidence label skeleton */}
                  <div className="mt-4">
                    <div className="w-24 h-3 bg-[#e6eef5] animate-pulse mb-2" />
                    <div className="flex items-center gap-3">
                      {Array.from({ length: 5 }).map((_, k) => (
                        <div key={k} className="w-14 h-3 bg-[#e6eef5] animate-pulse" />
                      ))}
                    </div>
                  </div>

                  {/* Undo button skeleton bottom-left */}
                  <div className="mt-4">
                    <div className="w-20 h-8 bg-[#e6eef5] animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : paginatedQuestions.length === 0 ? (
            <p className="text-center text-[#919191] py-8 text-sm">No questions found.</p>
          ) : paginatedQuestions.map((q) => (
            <QuestionCard key={q.id} question={q} />
          ))}
        </div>

        {/* Bottom: Approve Selected / Reject Selected + Pagination */}
        <div className="flex flex-col items-center gap-4 mt-6">
          {anySelected && (
            <div className="flex gap-3">
              <button
                onClick={handleApproveSelected}
                className="px-5 py-2 text-sm font-semibold bg-[#0077b5] text-white hover:bg-[#0077b5] transition-colors"
              >
                Approve Selected
              </button>
              <button
                onClick={handleRejectSelected}
                className="px-5 py-2 text-sm font-semibold bg-[#F47F2E] text-white hover:bg-[#F47F2E] transition-colors"
              >
                Reject Selected
              </button>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center border border-[#919191] text-[#919191] hover:bg-[#919191]/10 disabled:opacity-40"
              >
                <FiChevronLeft className="text-sm" />
              </button>
              {(() => {
                const pages = [];
                const showEllipsisStart = currentPage > 3;
                const showEllipsisEnd = currentPage < totalPages - 2;
                
                // Always show first page
                pages.push(1);
                
                // Show ellipsis after first page if needed
                if (showEllipsisStart) {
                  pages.push('ellipsis-start');
                }
                
                // Show pages around current page
                for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                  if (!pages.includes(i)) {
                    pages.push(i);
                  }
                }
                
                // Show ellipsis before last page if needed
                if (showEllipsisEnd) {
                  pages.push('ellipsis-end');
                }
                
                // Always show last page if more than 1 page
                if (totalPages > 1 && !pages.includes(totalPages)) {
                  pages.push(totalPages);
                }
                
                return pages.map((page, index) => {
                  if (page === 'ellipsis-start' || page === 'ellipsis-end') {
                    return (
                      <span key={page} className="text-[#919191] text-sm px-1">
                        ···
                      </span>
                    );
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-8 h-8 flex items-center justify-center text-sm font-semibold transition-colors ${currentPage === page
                        ? 'bg-[#F47F2E] text-white'
                        : 'border border-[#919191] text-[#919191] hover:bg-[#919191]/10'
                        }`}
                    >
                      {page}
                    </button>
                  );
                });
              })()}
              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
                className="w-8 h-8 flex items-center justify-center border border-[#919191] text-[#919191] hover:bg-[#919191]/10 disabled:opacity-40"
              >
                <FiChevronRight className="text-sm" />
              </button>
            </div>
          )}
        </div>
      </div>


    </div>
  );
};

// Wrapper component with context provider
const ReviewerQuestionnairesWithProvider = () => {
  return (
    <ReviewerProvider>
      <ReviewerQuestionnairesContent />
    </ReviewerProvider>
  );
};

export default ReviewerQuestionnairesWithProvider;


